import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { Formik } from "formik";
import { useSearchParams } from "react-router-dom";
import useAuthentication from "hooks/useAuthentication";
import { useTheme } from 'contexts/ThemeContext';
import { AuthPageLayout } from 'components/common/AuthPageLayout';

function Login() {
  const { loginWithEmailAndPassword, verifyMfa, resendMfa, resetPassword } = useAuthentication();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const orgMismatch = searchParams.get("org_mismatch") === "1";
  const [loginError, setLoginError] = React.useState('');
  const [resetPasswordError, setResetPasswordError] = React.useState('');
  const [mfaToken, setMfaToken] = React.useState<string | null>(null);
  const [mfaCode, setMfaCode] = React.useState('');
  const [mfaSubmitting, setMfaSubmitting] = React.useState(false);
  const [resendSubmitting, setResendSubmitting] = React.useState(false);
  const [resendMessage, setResendMessage] = React.useState('');
  const emailInputRef = React.useRef<HTMLInputElement>(null);

  const fieldSx = {
    backgroundColor: '#f9f9f9',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: theme.customer.secondaryColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.customer.primaryColor,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.customer.primaryColor,
    },
  };

  const primaryButtonSx = {
    backgroundColor: theme.customer.primaryColor,
    borderRadius: `${theme.customer.borderRadius || 8}px`,
    fontFamily: theme.customer.fontFamily || 'inherit',
    '&:hover': {
      backgroundColor: theme.customer.primaryColor,
      opacity: 0.9,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${theme.customer.primaryColor}40`,
    },
    transition: 'all 0.2s ease-in-out',
  };

  const linkSx = {
    alignSelf: 'center',
    color: theme.customer.secondaryColor,
    textDecoration: 'none',
    '&:hover': {
      color: theme.customer.primaryColor,
      textDecoration: 'underline',
    },
    transition: 'color 0.2s ease-in-out',
  };

  return (
    <AuthPageLayout pageTitle={mfaToken ? "Check your email" : "Login"}>
      {orgMismatch && !mfaToken && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You were signed out because this portal address does not match your
          organization. Use the link your team provided, or check the subdomain
          in the URL (for example, your-company.electronicsalpha.app).
        </Alert>
      )}
      {mfaToken ? (
        <Box display="flex" flexDirection="column">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
            Enter the 6-digit verification code we sent to your email.
          </Typography>
          <FormControl variant="standard" sx={{ mb: 2 }}>
            <TextField
              autoFocus
              variant="outlined"
              label="Verification code"
              value={mfaCode}
              onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
              sx={fieldSx}
            />
          </FormControl>
          {loginError && (
            <Typography color="error" sx={{ mt: 1, mb: 1, textAlign: "center" }}>
              {loginError}
            </Typography>
          )}
          {resendMessage && (
            <Typography color="text.secondary" sx={{ mt: 1, mb: 1, textAlign: "center" }}>
              {resendMessage}
            </Typography>
          )}
          <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              disabled={mfaSubmitting || mfaCode.length !== 6}
              onClick={async () => {
                try {
                  setLoginError("");
                  setResendMessage("");
                  setMfaSubmitting(true);
                  await verifyMfa(mfaToken, mfaCode);
                } catch (error: unknown) {
                  setLoginError(error instanceof Error ? error.message : "Invalid verification code");
                  setMfaSubmitting(false);
                }
              }}
              sx={primaryButtonSx}
            >
              Verify
            </Button>
            <Button
              variant="text"
              disabled={resendSubmitting}
              onClick={async () => {
                try {
                  setLoginError("");
                  setResendSubmitting(true);
                  const nextToken = await resendMfa(mfaToken);
                  setMfaToken(nextToken);
                  setResendMessage("A new code was sent to your email.");
                } catch (error: unknown) {
                  setLoginError(
                    error instanceof Error ? error.message : "Unable to resend verification code"
                  );
                } finally {
                  setResendSubmitting(false);
                }
              }}
              sx={{ ...linkSx, textTransform: "none" }}
            >
              Resend code
            </Button>
          </Box>
          <Button
            variant="text"
            onClick={() => {
              setMfaToken(null);
              setMfaCode("");
              setLoginError("");
              setResendMessage("");
            }}
            sx={{ mt: 2, textTransform: "none", color: theme.customer.primaryColor }}
          >
            Back to login
          </Button>
        </Box>
      ) : (
      <Formik
        initialValues={{ email: '', password: '' }}
        onSubmit={async (values) => {
          if (values.email && values.password) {
            try {
              setLoginError('');
              const result = await loginWithEmailAndPassword(values.email, values.password);
              if (result.mfaRequired) {
                setMfaToken(result.mfaToken);
                setMfaCode('');
              }
            } catch (error: unknown) {
              if (error instanceof Error) {
                setLoginError('Please check your email and password');
              } else {
                setLoginError('An unexpected error occurred');
              }
            }
          }
        }}
        validationSchema={yup.object({
          email: yup
            .string()
            .email('Invalid Email')
            .required('Email Required'),
          password: yup
            .string()
            .min(6, 'Password must be at least 6 characters')
            .required('Password Required'),
        })}
      >
        {({ getFieldProps, errors, handleSubmit, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <Box display='flex' flexDirection='column'>
              <FormControl variant='standard' sx={{ mb: 2 }}>
                <TextField
                  inputRef={emailInputRef}
                  type='email'
                  variant='outlined'
                  label='Email'
                  {...getFieldProps('email')}
                  id='email'
                  error={
                    (touched.email && !!errors.email) || !!resetPasswordError
                  }
                  helperText={
                    (touched.email && errors.email) || resetPasswordError
                  }
                  sx={fieldSx}
                />
              </FormControl>
              <FormControl variant='standard' sx={{ mt: 2 }}>
                <TextField
                  type='password'
                  variant='outlined'
                  label='Password'
                  {...getFieldProps('password')}
                  id='password'
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  sx={fieldSx}
                />
              </FormControl>

              {loginError && (
                <Typography color='error' sx={{ mt: 2, textAlign: 'center' }}>
                  {loginError}
                </Typography>
              )}

              <Box
                display='flex'
                justifyContent='space-between'
                sx={{ mt: 2 }}
              >
                <Button
                  variant='contained'
                  size='large'
                  type='submit'
                  sx={primaryButtonSx}
                >
                  Login
                </Button>
                <Link
                  href='#'
                  onClick={() => {
                    setResetPasswordError('');
                    const emailSchema = yup
                      .string()
                      .email('Invalid Email')
                      .required('Email Required');

                    try {
                      emailSchema.validateSync(values.email);
                      resetPassword(values.email);
                    } catch (error) {
                      if (error instanceof Error) {
                        setResetPasswordError(error.message);
                        emailInputRef.current?.focus();
                      }
                    }
                  }}
                  sx={linkSx}
                >
                  Reset Password
                </Link>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
      )}
    </AuthPageLayout>
  );
}

export default Login;
